import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import {
  BookCopy,
  Layers,
  RotateCcw,
  Search,
  Sparkles,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import CaseStudyGrid from '../components/CaseStudyGrid';
import './Cases.css';

const FALLBACK_AREAS = [
  'Profitability',
  'SWOT',
  '3Cs',
  '4Ps',
  'Market Sizing',
  'Marketing Strategy',
  "Porter's Five Forces",
  'Growth Strategy',
  'Marketing Entry/Expansion',
  'Synergies',
  'Cost Modeling',
  'BCG Growth Share Matrix',
];

const normalize = (value) => String(value || '').trim().toLowerCase();

const CaseStudySelection = () => {
  const [caseStudies, setCaseStudies] = useState([]);
  const [topics, setTopics] = useState([]);
  const [difficultyLevels, setDifficultyLevels] = useState([]);
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [selectedArea, setSelectedArea] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const url = process.env.REACT_APP_BACK_END_URL;
  const { getToken } = useAuth();

  const fetchCaseStudies = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const token = await getToken();
      const response = await fetch(`${url}/api/mainCases`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Unable to load the case library.');

      const data = await response.json();
      setCaseStudies(data.caseStudies || []);
      setTopics(data.topics || []);
      setDifficultyLevels(data.difficultyLevels || []);
    } catch (requestError) {
      console.error('Error loading case studies:', requestError);
      setError('We could not load the case library. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [getToken, url]);

  useEffect(() => {
    fetchCaseStudies();
  }, [fetchCaseStudies]);

  const availableAreas = useMemo(() => {
    const source = topics.length
      ? topics
      : caseStudies.map((study) => study.area).filter(Boolean);
    const unique = [...new Set(source)];
    return unique.length ? unique : FALLBACK_AREAS;
  }, [caseStudies, topics]);

  const availableDifficulties = useMemo(() => {
    const source = difficultyLevels.length
      ? difficultyLevels
      : caseStudies.map((study) => study.difficultyLevel).filter(Boolean);
    return [...new Set(source)];
  }, [caseStudies, difficultyLevels]);

  const filteredCases = useMemo(() => {
    const query = normalize(searchTerm);
    return caseStudies.filter((study) => {
      const matchesDifficulty = selectedDifficulty === 'all'
        || normalize(study.difficultyLevel) === normalize(selectedDifficulty);
      const matchesArea = selectedArea === 'all'
        || normalize(study.area) === normalize(selectedArea);
      const searchableText = [study.title, study.area, study.objective, study.companyBackground]
        .map(normalize)
        .join(' ');
      return matchesDifficulty && matchesArea && searchableText.includes(query);
    });
  }, [caseStudies, searchTerm, selectedArea, selectedDifficulty]);

  const filtersActive = Boolean(
    searchTerm || selectedDifficulty !== 'all' || selectedArea !== 'all'
  );

  const resetFilters = () => {
    setSearchTerm('');
    setSelectedDifficulty('all');
    setSelectedArea('all');
  };

  return (
    <div className="case-library">
      <Navbar />

      <header className="case-library-hero">
        <div className="case-library-shell case-library-hero-grid">
          <div className="case-library-hero-copy">
            <span className="case-kicker"><Sparkles size={16} /> Interactive case library</span>
            <h1>Practice the work, not just the theory.</h1>
            <p>
              Choose a consulting-style problem, build a structured recommendation, and receive focused feedback on how you think and communicate.
            </p>
          </div>
          <div className="case-library-summary" aria-label="Case library summary">
            <div><span>Cases</span><strong>{caseStudies.length || '—'}</strong></div>
            <div><span>Frameworks</span><strong>{availableAreas.length}</strong></div>
            <div><span>Feedback</span><strong>AI-guided</strong></div>
          </div>
        </div>
      </header>

      <main className="case-library-shell case-library-main">
        <section className="case-filter-panel" aria-label="Filter case studies">
          <div className="case-filter-heading">
            <div>
              <span>Find your next case</span>
              <strong>{isLoading ? 'Loading cases' : `${filteredCases.length} available`}</strong>
            </div>
            {filtersActive && (
              <button type="button" className="case-reset-button" onClick={resetFilters}>
                <RotateCcw size={15} /> Reset filters
              </button>
            )}
          </div>

          <div className="case-filter-controls">
            <label className="case-search-field">
              <Search size={19} />
              <span className="sr-only">Search case studies</span>
              <input
                type="search"
                placeholder="Search by title, framework, or objective"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
              {searchTerm && (
                <button type="button" onClick={() => setSearchTerm('')} aria-label="Clear search">×</button>
              )}
            </label>

            <label className="case-select-field">
              <span>Difficulty</span>
              <select value={selectedDifficulty} onChange={(event) => setSelectedDifficulty(event.target.value)}>
                <option value="all">All levels</option>
                {availableDifficulties.map((level) => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
            </label>

            <label className="case-select-field">
              <span>Framework</span>
              <select value={selectedArea} onChange={(event) => setSelectedArea(event.target.value)}>
                <option value="all">All frameworks</option>
                {availableAreas.map((area) => (
                  <option key={area} value={area}>{area}</option>
                ))}
              </select>
            </label>
          </div>
        </section>

        <div className="case-results-heading">
          <div>
            <BookCopy size={18} />
            <span>{filtersActive ? 'Filtered case set' : 'Complete case set'}</span>
          </div>
          <p><Layers size={15} /> Work at your pace. Feedback is saved to your performance history.</p>
        </div>

        {error ? (
          <section className="case-library-state is-error">
            <h2>The library did not load.</h2>
            <p>{error}</p>
            <button type="button" onClick={fetchCaseStudies}>Try again</button>
          </section>
        ) : isLoading ? (
          <div className="case-card-skeletons" aria-label="Loading case studies">
            {[0, 1, 2, 3, 4, 5].map((item) => <div key={item} />)}
          </div>
        ) : (
          <CaseStudyGrid caseStudies={filteredCases} onReset={filtersActive ? resetFilters : undefined} />
        )}
      </main>
    </div>
  );
};

export default CaseStudySelection;
